/*
 * The main application component
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  Component,
  OnDestroy,
  OnInit, ViewEncapsulation
} from '@angular/core';
import {
  AuthService,
  ContactEmailService,
  HealthService,
  LoadingIndicatorService,
  OidcAuthService,
  UserService
} from './shared/services';
import {
  NavigationEnd,
  ResolveEnd,
  ResolveStart,
  Router
} from '@angular/router';
import {
  BehaviorSubject,
  Subject,
  Subscription
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UiConfigService } from 'app/shared/services/ui-config.service';
import {
  extractRedirectPathFromSearch,
  isIframeModeEnabled,
} from 'app/shared/lib';

@Component ( {
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
} )
export class AppComponent implements OnInit, OnDestroy {
  title = 'GB Reporter';
  isOnline: boolean;
  loading = false;
  canChangeClient: BehaviorSubject<boolean>;
  private loadingSubscription: Subscription;
  private destroyed$ = new Subject ();
  showGlobalError = false;
  whiteLabelLogoUri: string;
  hideChromeForIframeView = false;
  // The update email modal must be finished first, so the header and footer behind it take no focus or clicks
  chromeInert = false;

  constructor (
    private healthService: HealthService,
    private authService: AuthService,
    private router: Router,
    private loadingIndicatorService: LoadingIndicatorService,
    private oidcService: OidcAuthService,
    private userService: UserService,
    private uiConfigService: UiConfigService,
    private contactEmailService: ContactEmailService,
  ) {
    this.canChangeClient = this.userService.canChangeClient;
    this.updateHideChromeForIframeView ();
  }

  ngOnInit (): void {
    this.whiteLabelLogoUri = this.uiConfigService.whiteLabelLogoUri;
    this.setCustomCss ();

    this.healthService.isBackendOnline ()
      .subscribe ( value => {
        value ? console.log ( 'Backend is online' ) : console.warn ( 'No connection to backend' );
        this.isOnline = value;
      } );
    this.loadingSubscription = this.loadingIndicatorService.isLoading
      .subscribe ( val => setTimeout ( () => this.loading = val ) );

    this.router.events
      .pipe ( takeUntil ( this.destroyed$ ) )
      .subscribe ( event => {
        this.updateHideChromeForIframeView ();

        if ( event instanceof NavigationEnd ) {
          this.chromeInert = this.isUpdateEmailUrl ( event.urlAfterRedirects );
        }
        if ( event instanceof ResolveStart ) {
          this.showGlobalError = true;
        }
        if ( event instanceof ResolveEnd ) {
          this.showGlobalError = false;
        }
      } );
  }

  get isAuthenticated () {
    return this.authService.isAuthenticated;
  }

  private updateHideChromeForIframeView (): void {
    this.hideChromeForIframeView = this.isIframePropertiesUrl ( this.getCurrentUrl () );
  }

  private getCurrentUrl (): string {
    return `${ location.pathname }${ location.search }`;
  }

  private isUpdateEmailUrl ( url: string ): boolean {
    return this.router.parseUrl ( url ).root.children.primary?.segments.map ( segment => segment.path ).join ( '/' ) === 'update-email';
  }

  private static readonly IFRAME_CHROME_HIDDEN_PATHS = [ 'incident/properties', 'ss-validate-vehicle-plate' ];

  private isIframePropertiesUrl ( url: string ): boolean {
    const urlTree = this.router.parseUrl ( url );
    const currentPath = urlTree.root.children.primary?.segments.map ( segment => segment.path ).join ( '/' );

    if ( AppComponent.IFRAME_CHROME_HIDDEN_PATHS.includes ( currentPath ) && isIframeModeEnabled ( urlTree.queryParams, new URL ( url, location.origin ).search ) ) {
      return true;
    }

    const redirectPath = extractRedirectPathFromSearch ( new URL ( url, location.origin ).search ) ?? urlTree.queryParams.redirectPath;

    if ( !redirectPath ) {
      return false;
    }

    const redirectUrlTree = this.router.parseUrl ( redirectPath );
    const redirectTargetPath = redirectUrlTree.root.children.primary?.segments.map ( segment => segment.path ).join ( '/' );

    return AppComponent.IFRAME_CHROME_HIDDEN_PATHS.includes ( redirectTargetPath ) &&
      isIframeModeEnabled ( redirectUrlTree.queryParams, new URL ( redirectPath, location.origin ).search );
  }

  async logout () {
    await this.authService.logout ().toPromise ();
    if ( await this.oidcService.isLoggedIn () ) {
      await this.oidcService.logout ();
    } else {
      const urlSegmentKey = this.uiConfigService.urlSegmentKey ? `/${ this.uiConfigService.urlSegmentKey }` : '';
      await this.router.navigate ( [ `${ urlSegmentKey }/login` ] );
    }
  }

  loginAdmin () {
    if ( this.contactEmailService.isUpdateRequired ) {
      this.router.navigate ( [ '/update-email' ] ).then ();
      return;
    }
    this.userService.loginAdmin ( this.authService.accessToken );
  }

  setCustomCss () {
    const customCss = this.uiConfigService.customCss;
    if ( customCss ) {
      const customCssObject = JSON.parse ( customCss );
      const customCssObjectWithoutNullValues = Object.keys ( customCssObject )
        .filter ( key => customCssObject[ key ] !== null )
        .reduce ( ( obj, key ) => {
          obj[ key ] = customCssObject[ key ];
          return obj;
        }, {} );
      delete customCssObjectWithoutNullValues[ 'additionalCss' ];
      const customCssObjectWithCssVariables = Object.keys ( customCssObjectWithoutNullValues )
        .reduce ( ( obj, key ) => {
          obj[ `--${ key.replace ( /([A-Z])/g, '-$1' ).toLowerCase () }` ] = customCssObjectWithoutNullValues[ key ];
          return obj;

        }, {} );
      Object.keys ( customCssObjectWithCssVariables ).forEach ( key => {
        document.documentElement.style.setProperty ( key, customCssObjectWithCssVariables[ key ] );
      } );
      if ( customCssObject.additionalCss ) {
        const style = document.createElement ( 'style' );
        style.innerHTML = customCssObject.additionalCss;
        document.head.appendChild ( style );
      }
    }
  }

  ngOnDestroy () {
    this.loadingSubscription?.unsubscribe ();
  }
}
